// Per spec/components.md §8. Logo-led sponsor card. **P-037.23 / D-131
// rewrite.** Forks the post-D-130 EventCard shape and substitutes
// sponsor-specific fields:
//   [10 px navy bar]
//   [logo 190 × 100, centered — with inset shadow on the cell]
//   Sponsor Name (16 px navy h3; wrapped in a link at ctx.linkColor when
//     sponsorWebsiteUrl is set — the name IS the CTA)
//   [optional caption — plain 14 px text, EventCard-location parity]
//   [16 px bottom spacer]
//
// **Sponsor-name-as-link.** Pre-D-131 SponsorCard rendered a separate
// "Visit Website →" link below the description; that affordance is gone
// and the sponsor name itself carries the CTA semantics when
// `sponsorWebsiteUrl` is present. When unset, the name renders as plain
// h3 (no link decoration). Same pattern as MemberCard's conditional
// name link (D-117).
//
// **Image-optional + fallback.** Same logic as EventCard post-D-131:
// `logoUrl` undefined → render `/fallback-image.png` (decorative
// `alt=""`) in the same image slot.
//
// **Image-cell edge: inset shadow on the cell (P-037.29 / D-137 §item 4
// revised).** Reverts D-133's 1 px IMG border. Cell carries
// `box-shadow: inset 0 0 16px rgba(0, 0, 0, 0.35);` (no padding ring,
// no IMG-shrink — Banks's correction post-initial-D-137); IMG renders
// at full 190 × 100 edge-to-edge. The shadow is mostly hidden behind
// full-bleed staff logos (same as the original D-131 §13 state) but
// renders where the logo has transparent edges. Outlook desktop drops
// `box-shadow` silently (progressive enhancement).
//
// Layout dimensions (190 × 100 outer, 10 px navy bar, 16 px gaps, 14 px
// caption, 16 px bottom spacer) mirror EventCard exactly — chapter-
// sponsors uses the same 3-col grid (`30 / 190 / 25 / 190 / 25 / 190 /
// 30`) so the two Blocks visually compose with shared rhythm.

import { html } from '../render'
import { tokens } from '../tokens'
import { heading } from '../primitives/heading'
import { text } from '../primitives/text'
import { image } from '../primitives/image'
import { link } from '../primitives/link'
import { spacer } from '../primitives/spacer'
import { HtmlString } from '../types'
import { FALLBACK_IMAGE_SRC } from './helpers'

export interface SponsorCardProps {
  sponsorName: string
  logoUrl?: string
  logoAlt?: string
  caption?: string
  sponsorWebsiteUrl?: string
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used on the sponsor-name anchor when `sponsorWebsiteUrl` is
   *  set. Defaults to `tokens.color['link-default']`. */
  linkColor?: string
}

// P-037.29 D-137 §item 4 (revised at post-Phase-Close §item G): IMG
// renders at full card dimensions (190 × 100). Inset shadow is baked
// into the image bytes at upload time (`bakeInsetShadow: true` on
// `chapter-sponsors.sponsors.logoUrl`); fallback arm uses the pre-
// baked `/fallback-image.png` static asset. No cell-level CSS shadow
// needed — fully redundant for both arms.
const IMAGE_WIDTH = 190
const IMAGE_HEIGHT = 100
const NAVY_BAR_HEIGHT = 10
const CARD_PAD_X = tokens.space[4]
const IMAGE_TO_NAME_GAP = tokens.space[4]
const SECTION_GAP_14 = 14
const NAVY = tokens.color['accent-navy-deep']
const BOTTOM_SPACER = spacer({ height: tokens.space[4] })

export function sponsorCard({
  sponsorName,
  logoUrl,
  logoAlt,
  caption,
  sponsorWebsiteUrl,
  linkColor,
}: SponsorCardProps): HtmlString {
  // 10 px navy strip flush above logo — bulletproof image-flush attrs.
  // P-048.5 / D-148 — wrap the bar in a 190px-wide centered inner table
  // to match the logo width on mobile collapse. See `event-card.ts`
  // navyBarRow comment for the full rationale + 3-layer belt-and-
  // suspenders centering pattern.
  // prettier-ignore
  const navyBarRow = html`<tr><td align="center" style="padding: 0; font-size: 0; line-height: 0;"><table role="presentation" width="${IMAGE_WIDTH}" cellpadding="0" cellspacing="0" border="0" align="center" style="margin: 0 auto;"><tr><td height="${NAVY_BAR_HEIGHT}" bgcolor="${NAVY}" style="line-height: ${NAVY_BAR_HEIGHT}px; font-size: 0;">&nbsp;</td></tr></table></td></tr>`
  // Logo: 190 × 100 with decorative `alt=""` on the fallback arm; staff-
  // uploaded logo's alt falls back to sponsorName.
  const logoHtml =
    logoUrl !== undefined
      ? image({
          src: logoUrl,
          alt: logoAlt ?? sponsorName,
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          align: 'center',
        })
      : image({
          src: FALLBACK_IMAGE_SRC,
          alt: '',
          width: IMAGE_WIDTH,
          height: IMAGE_HEIGHT,
          align: 'center',
        })
  // 16 px bold navy sponsor name. When sponsorWebsiteUrl is set, the
  // heading's text is a link anchor at ctx.linkColor (heading() accepts
  // `string | HtmlString` per D-131; the link primitive's HtmlString
  // output composes inside the h3 with byte-identical surrounding form
  // to the no-link arm). When unset, plain text — no anchor.
  const nameText =
    sponsorWebsiteUrl !== undefined
      ? link({
          text: sponsorName,
          href: sponsorWebsiteUrl,
          color: linkColor ?? tokens.color['link-default'],
          weight: tokens.type.weight.bold,
        })
      : sponsorName
  const nameHtml = heading({
    text: nameText,
    level: 3,
    size: tokens.type.size.base,
    weight: tokens.type.weight.bold,
    color: NAVY,
  })
  const captionHtml =
    caption !== undefined ? text({ text: caption, size: tokens.type.size.sm }) : undefined
  // prettier-ignore
  const captionRow = captionHtml !== undefined
    ? html`<tr><td style="padding: ${SECTION_GAP_14}px ${CARD_PAD_X}px 0;">${captionHtml}</td></tr>`
    : undefined

  // P-048.6 §B / D-149 — outer card table fixed at IMAGE_WIDTH (190) +
  // centered. See `event-card.ts` for the full rationale.
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${IMAGE_WIDTH}" align="center" style="margin: 0 auto;">${navyBarRow}<tr><td>${logoHtml}</td></tr><tr><td style="padding: ${IMAGE_TO_NAME_GAP}px ${CARD_PAD_X}px 0;">${nameHtml}</td></tr>${captionRow}${BOTTOM_SPACER}</table>`
}
